import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import { PageHeader } from "@/components/shared/PageHeader";
import { supplierKeys } from "@/lib/queryKeys";
import { SupplierForm } from "@/pages/suppliers/SupplierForm";
import type { SupplierFormValues } from "@/pages/suppliers/supplier-form-shared";
import {
  createSupplier,
  type CreateSupplierPayload,
} from "@/services/supplierService";

function toCreatePayload(values: SupplierFormValues): CreateSupplierPayload {
  return {
    legalName: values.legalName.trim(),
    displayName: values.displayName?.trim() || undefined,
    documentType: values.documentType,
    rut: values.rut?.trim() || undefined,
    countryCode: values.countryCode?.trim().toUpperCase() || undefined,
    docRecepExt: values.docRecepExt?.trim() || undefined,
    address: values.address?.trim() || undefined,
    ciudad: values.ciudad?.trim() || undefined,
    departamento: values.departamento?.trim() || undefined,
    email: values.email?.trim() || undefined,
    phone: values.phone?.trim() || undefined,
    defaultCurrency: values.defaultCurrency,
    defaultCategory: values.defaultCategory?.trim() || undefined,
    autoAckCfeOnReceive: values.autoAckCfeOnReceive,
    notes: values.notes?.trim() || undefined,
  };
}

export function NewSupplierPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (values: SupplierFormValues) =>
      createSupplier(toCreatePayload(values)),
    onSuccess: (supplier) => {
      toast.success(t("suppliers.new.created", "Proveedor agregado."));
      queryClient.invalidateQueries({ queryKey: supplierKeys.all });
      navigate(`/suppliers/${supplier.id}`);
    },
    onError: () => {
      toast.error(
        t(
          "suppliers.new.error",
          "No pudimos agregar el proveedor. Probá de nuevo.",
        ),
      );
    },
  });

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        title={t("suppliers.new.title", "Nuevo proveedor")}
        onBack={() => navigate("/suppliers")}
      />
      <SupplierForm
        submitLabel={t("suppliers.new.submit", "Agregar proveedor")}
        isSubmitting={createMutation.isPending}
        onCancel={() => navigate("/suppliers")}
        onSubmit={(values) => createMutation.mutate(values)}
      />
    </div>
  );
}
